import {
  faCheck,
  faFastForward,
  faPencil,
  faSquare,
  faThumbsDown,
  IconDefinition,
} from "@fortawesome/duotone-light-svg-icons"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  Checkbox,
  JoonColorExpanded,
  JoonUIColor,
  QuestInstance,
  QuestStatus,
  QuestType,
  SPACING,
  TextArea,
  TextButton,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useViewQuestModalStore } from "./useViewQuestModalStore"
import AttachmentItem from "../../../components/attachmentItem/AttachmentItem"
import UserBadge from "../../../components/badges/userBadge/UserBadge"
import { CloseButton } from "../../../components/closeButton/CloseButton"
import { SelectRow } from "../../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../../components/selectRowContainer/SelectRowContainer"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import {
  completeQuestStatuses,
  openQuestStatuses,
  QUERY_KEYS,
  rejectedQuestStatuses,
} from "../../../constants"
import useMobile from "../../../hooks/useMobile"
import { useSubscription } from "../../../hooks/useSubscription"
import { useFamilyQuery } from "../../../networking/queries"
import {
  completeQuest,
  rejectQuest,
  skipQuests,
  undoVerifyQuest,
  updateQuestNotes,
} from "../../../networking/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { queryClient } from "../../../util/queryClient"
import {
  getDaysFromRRuleString,
  getDayRepetitionString,
  getSuccessMessageFromStatuses,
  getErrorMessageFromStatuses,
  checkStatusEquality,
  isCoachQuest,
} from "../../../util/util"
import CareTeamAvatar from "../../care/CareTeamAvatar"
import useCareTeam from "../../care/useCareTeam"
import { useAddEditQuestStore } from "../addEditQuestModal/useAddEditQuestStore"
import { useFirstQuestCompleteModalStore } from "../useFirstQuestCompleteModalStore"

interface QuestActionsModalStoreProps {
  isOpen: boolean
  showCard: boolean
  questInstance: QuestInstance | null
  selectedStatus: QuestStatus | null
  message: string
  onOpen: (questInstance: QuestInstance) => void
  onClose: () => void
  setShowCard: (showCard: boolean) => void
  setSelectedStatus: (status: QuestStatus | null) => void
  setMessage: (message: string) => void
}

export const useQuestActionsModalStore = create<QuestActionsModalStoreProps>()(
  (set) => ({
    isOpen: false,
    showCard: false,
    questInstance: null,
    selectedStatus: null,
    message: "",
    onOpen: (questInstance: QuestInstance) =>
      set({
        questInstance,
        selectedStatus: questInstance.status,
        isOpen: true,
        showCard: true,
      }),
    onClose: () => {
      set({
        questInstance: null,
        isOpen: false,
        selectedStatus: null,
        message: "",
        showCard: false,
      })
    },
    setShowCard: (showCard: boolean) => set({ showCard }),
    setSelectedStatus: (status: QuestStatus | null) =>
      set({ selectedStatus: status }),
    setMessage: (message: string) => set({ message }),
  })
)

export const QuestActionsModal = () => {
  const [isSavingUpdates, setIsSavingUpdates] = useState(false)
  const {
    isOpen,
    questInstance,
    showCard,
    selectedStatus,
    message,
    onClose,
    setSelectedStatus,
    setMessage,
    setShowCard,
  } = useQuestActionsModalStore()
  const {
    isOpen: isOpenViewQuestModal,
    onOpen: onOpenViewQuestModal,
    onClose: onCloseViewQuestModal,
  } = useViewQuestModalStore()
  const { onOpen: onOpenAddEditQuestModal } = useAddEditQuestStore()
  const { onOpen: onOpenFirstQuestCompleteModalIfApplicable } =
    useFirstQuestCompleteModalStore()
  const { checkRemainingFreeQuests } = useSubscription()
  const { data: family } = useFamilyQuery()
  const { careTeam } = useCareTeam()
  const isMobile = useMobile()
  const { t } = useTranslation()
  const userProfile = family?.profiles.find(
    (profile) => profile.user.id === questInstance?.user_id
  )
  const isRedeemed = questInstance?.status === QuestStatus.REDEEMED
  const byDay = getDaysFromRRuleString(questInstance?.series?.recurrence || "")
  const subtitle = getDayRepetitionString(
    byDay,
    questInstance?.series?.reminder_time || null
  )
  const isParentQuest = userProfile?.role === UserRole.PARENT
  const isParentCareQuest =
    isParentQuest &&
    (questInstance?.series?.assigner_profile?.role === UserRole.THERAPIST ||
      isCoachQuest(questInstance))
  const questIsRepeating = questInstance?.series.type === QuestType.REPEATING
  const messageHasChanged = isParentQuest
    ? (questInstance?.completion_notes || "") !== message
    : (questInstance?.verification_notes || "") !== message
  const showSaveButton =
    questInstance?.status !== selectedStatus || messageHasChanged
  const coachProfile = careTeam?.find(
    (profile) =>
      profile.user.id === questInstance?.series?.assigner_profile?.user_id
  )

  const statusOptions: {
    label: string
    value: QuestStatus
    icon: IconDefinition
    color: JoonColorExpanded
    isSelected: boolean
  }[] = [
    {
      label: t("STATUS_INCOMPLETE"),
      value: QuestStatus.OPEN,
      icon: faSquare,
      color: JoonUIColor.icon.light,
      isSelected:
        !!selectedStatus && openQuestStatuses.includes(selectedStatus),
    },
    ...(questIsRepeating
      ? [
          {
            label: t("QB_TAB_SKIPPED"),
            value: QuestStatus.SKIPPED,
            icon: faFastForward,
            color: JoonUIColor.icon.accent,
            isSelected: selectedStatus === QuestStatus.SKIPPED,
          },
        ]
      : []),
    ...(userProfile?.role !== UserRole.PARENT
      ? [
          {
            label: t("QB_TAB_REJECTED"),
            value: QuestStatus.REJECTED,
            icon: faThumbsDown,
            color: JoonUIColor.semantic.destructive,
            isSelected:
              !!selectedStatus &&
              rejectedQuestStatuses.includes(selectedStatus),
          },
        ]
      : []),
  ]
  const completedOption = {
    label: t("QB_TAB_COMPLETED"),
    value: QuestStatus.COMPLETED,
    icon: faCheck,
    color: JoonUIColor.semantic.success,
    isSelected:
      !!selectedStatus && completeQuestStatuses.includes(selectedStatus),
  }
  isRedeemed
    ? statusOptions.unshift(completedOption)
    : statusOptions.push(completedOption)

  const onEditQuestClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_EDIT_QUEST)
    onCloseViewQuestModal()
    onClose()
    onOpenAddEditQuestModal(questInstance?.series)
  }

  const onViewQuestDetailsClick = () => {
    onClose()
    if (questInstance && !isOpenViewQuestModal) {
      trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_QUEST_DETAILS)
      onOpenViewQuestModal(questInstance)
    }
  }

  const onSaveUpdatesClick = async () => {
    if (!questInstance || !selectedStatus) return

    const isAlreadyCompleted = completeQuestStatuses.includes(
      questInstance.status
    )
    const isAlreadyRejected = rejectedQuestStatuses.includes(
      questInstance.status
    )
    const isNewlyCompleted = completeQuestStatuses.includes(selectedStatus)
    const isNewlyRejected = rejectedQuestStatuses.includes(selectedStatus)
    const requiresVerification = isNewlyCompleted || isNewlyRejected
    const isAlreadyVerified = isAlreadyCompleted || isAlreadyRejected
    const showCheck =
      requiresVerification && !isAlreadyVerified && !isCoachQuest(questInstance)

    // only check for remaining free quests if the quest is not already completed/rejected and is not a coach quest
    if (showCheck) checkRemainingFreeQuests({})

    try {
      setIsSavingUpdates(true)

      if (!checkStatusEquality(questInstance.status, selectedStatus)) {
        switch (selectedStatus) {
          case QuestStatus.COMPLETED:
          case QuestStatus.VERIFIED:
          case QuestStatus.REDEEMED:
            await completeQuest({
              instanceId: questInstance.id,
              ...(isParentQuest
                ? { completionNotes: message }
                : { verificationNotes: message }),
            })
            break
          case QuestStatus.SKIPPED:
            await skipQuests({ questInstanceIds: [questInstance.id] })
            break
          case QuestStatus.REJECTED:
          case QuestStatus.REJECTED_VIEWED:
            await rejectQuest({
              instanceId: questInstance.id,
              data: { verification_notes: message },
            })
            break
          case QuestStatus.OPEN:
          case QuestStatus.RETRY:
            await undoVerifyQuest(questInstance.id)
            break
        }
      }
      const shouldUpdateNotes =
        messageHasChanged &&
        !completeQuestStatuses.includes(selectedStatus) &&
        !rejectedQuestStatuses.includes(selectedStatus)

      if (shouldUpdateNotes)
        await updateQuestNotes({
          questInstanceId: questInstance.id,
          isParentQuest,
          message,
        })

      await queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
      queryClient.invalidateQueries([QUERY_KEYS.QUEST_LOG])
      queryClient.invalidateQueries([QUERY_KEYS.USER])
      queryClient.invalidateQueries([QUERY_KEYS.COIN_TRANSACTIONS])
      queryClient.invalidateQueries([QUERY_KEYS.COIN_CUMULATIVE_STATS])
      queryClient.invalidateQueries([QUERY_KEYS.COINS_READY_TO_REDEEM])

      toast.success(
        getSuccessMessageFromStatuses({
          initialStatus: questInstance.status,
          newStatus: selectedStatus,
          isParentQuest,
        })
      )

      if (completeQuestStatuses.includes(selectedStatus)) {
        onOpenFirstQuestCompleteModalIfApplicable(userProfile?.user.name)
      }
    } catch (error) {
      toast.error(
        getErrorMessageFromStatuses({
          initialStatus: questInstance.status,
          newStatus: selectedStatus,
          isParentQuest,
        })
      )
    } finally {
      onClose()
      onCloseViewQuestModal()
      setIsSavingUpdates(false)
    }
  }

  const onModalClose = () => {
    setShowCard(false)
    setTimeout(() => onClose(), isMobile ? 100 : 0)
  }

  useEffect(() => {
    if (questInstance && isOpen) {
      setMessage(
        isParentQuest
          ? questInstance.completion_notes || ""
          : questInstance.verification_notes || ""
      )
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SlideUpModal
      isOpen={isOpen}
      showCard={showCard}
      onClose={onModalClose}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1002}
    >
      <div
        className="w-full flex flex-col justify-center items-center bg-bgXLightGray"
        style={{
          opacity: isSavingUpdates ? 0.5 : 1,
          pointerEvents: isSavingUpdates ? "none" : "auto",
          userSelect: isSavingUpdates ? "none" : "auto",
        }}
      >
        <CloseButton onClick={onModalClose} />
        <div className="w-full flex flex-col justify-center items-end border-b border-borderPrimary p-4 gap-4">
          <Typography
            variant="bodyBold"
            color={JoonUIColor.text.primary}
            textAlign="left"
          >
            {t("QA_MODIFY_TITLE")}
          </Typography>
          <SelectRowContainer>
            <SelectRow hideBorder showBorderBottom>
              <div className="w-full flex flex-row justify-between items-center gap-1">
                <div className="w-full flex flex-col justify-center items-start gap-1 overflow-hidden">
                  <Typography variant="bodyXSmall" textAlign="left">
                    {subtitle}
                  </Typography>

                  <div className="w-full flex flex-row justify-between items-center gap-1">
                    {isCoachQuest(questInstance) &&
                      coachProfile?.profile_image_url && (
                        <CareTeamAvatar profile={coachProfile} size="xs" />
                      )}
                    <Typography
                      variant="bodySmallBold"
                      textAlign="left"
                      style={{
                        textOverflow: "ellipsis",
                        width: "100%",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {questInstance?.series?.title.slice(0, 72)}
                    </Typography>
                  </div>

                  {questInstance && (
                    <UserBadge userId={questInstance.user_id} />
                  )}
                </div>

                <div className="w-fit flex flex-row gap-1 items-center">
                  <img
                    style={{ height: SPACING.space3 }}
                    src="/images/icons/coin-icon.png"
                    alt="coin"
                  />
                  <Typography variant="bodySmall">
                    {questInstance?.redeemable_reward}
                  </Typography>
                </div>
              </div>
            </SelectRow>
            <SelectRow
              hideBorder
              style={{ padding: `${SPACING.space1} ${SPACING.space2}` }}
            >
              <TextButton onClick={onEditQuestClick}>
                <div className="flex flex-row gap-1 items-center">
                  <FontAwesomeIcon
                    icon={faPencil}
                    fontSize={12}
                    color={JoonUIColor.icon.accent}
                  />
                  <Typography
                    variant="bodyXSmall"
                    color={JoonUIColor.text.primaryAccent}
                  >
                    {t("QA_EDIT_DETAILS")}
                  </Typography>
                </div>
              </TextButton>
              <TextButton onClick={onViewQuestDetailsClick}>
                <Typography
                  variant="bodyXSmall"
                  color={JoonUIColor.text.primaryAccent}
                >
                  {t("QA_VIEW_DETAILS")}
                </Typography>
              </TextButton>
            </SelectRow>
          </SelectRowContainer>
          {questInstance?.series?.resource && (
            <AttachmentItem
              name={questInstance.series.resource.title}
              url={questInstance.series.resource.resource_url}
            />
          )}
        </div>

        <div className="w-full flex flex-col justify-center items-start gap-4 p-4">
          <div className="w-full flex flex-col justify-center items-start gap-2">
            {isRedeemed && (
              <div className="flex flex-row justify-center items-center gap-2 bg-bgLightAccent rounded-md p-2">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  color={JoonUIColor.icon.accent}
                  fontSize={16}
                />
                <Typography
                  variant="bodySmall"
                  color={JoonUIColor.text.primary}
                >
                  {isParentQuest
                    ? t("QA_REDEEMED_SELF")
                    : t("QA_REDEEMED_CHILD")}
                </Typography>
              </div>
            )}
            <Typography variant="body" color={JoonUIColor.text.primary}>
              {t("QA_UPDATE_STATUS")}
            </Typography>
            <SelectRowContainer style={{ position: "relative" }}>
              {statusOptions.map((option, index) => (
                <SelectRow
                  key={option.label}
                  onClick={() => {
                    setSelectedStatus(option.value)
                  }}
                  hideBorder
                  showBorderBottom={index !== statusOptions.length - 1}
                  style={
                    isRedeemed
                      ? {
                          userSelect: "none",
                          opacity: !option.isSelected ? 0.5 : 1,
                          pointerEvents: "none",
                        }
                      : {}
                  }
                >
                  <div className="flex flex-row justify-center items-center gap-2">
                    <FontAwesomeIcon
                      icon={option.icon}
                      color={option.color}
                      fontSize={16}
                    />
                    <Typography
                      variant="bodySmall"
                      color={JoonUIColor.text.primary}
                    >
                      {option.label}
                    </Typography>
                  </div>
                  <Checkbox
                    inputType="radio"
                    hideBorder
                    name={option.label}
                    selected={option.isSelected}
                    onChange={() => {}}
                  />
                </SelectRow>
              ))}
            </SelectRowContainer>
          </div>
          <div className="w-full flex flex-col justify-center items-start gap-2">
            <Typography variant="body" color={JoonUIColor.text.primary}>
              {isParentQuest
                ? t("QA_HOW_DID_GO")
                : t("QA_LEAVE_NOTE", { name: userProfile?.user.name })}
            </Typography>
            {isParentCareQuest && (
              <Typography variant="bodySmall" color={JoonUIColor.text.primary}>
                {isCoachQuest(questInstance)
                  ? t("QA_REFLECTION_COACH")
                  : t("QA_REFLECTION_THERAPIST")}
              </Typography>
            )}
            <TextArea
              name="Message"
              placeholder={
                isParentQuest
                  ? t("QA_NOTE_PLACEHOLDER_PARENT")
                  : t("QA_NOTE_PLACEHOLDER_CHILD")
              }
              value={message}
              onChange={(e: any) => setMessage(e.target.value)}
              maxLength={255}
              showCounter={!isRedeemed}
              fullWidth
              style={{
                padding: SPACING.space3,
                borderRadius: SPACING.space2,
                border: `1px solid ${JoonUIColor.border.default}`,
                background: JoonUIColor.background.primaryNeutral,
                ...(isRedeemed && {
                  pointerEvents: "none",
                  userSelect: "none",
                }),
              }}
            />
          </div>
          {showSaveButton && (
            <Button
              text={t("QA_SAVE_UPDATES")}
              buttonType="primary"
              isLoading={isSavingUpdates}
              onClick={onSaveUpdatesClick}
              fullWidth
            />
          )}
        </div>
      </div>
    </SlideUpModal>
  )
}
